/**
 * Sazook - Reactive State Store (TypeScript)
 * Supports Multi-Item Cart & Battery Addon
 */

import { ProductSize, ProductColor, CartItem } from '../types';

export interface AppState {
  sizes: Record<string, ProductSize>;
  colors: Record<string, ProductColor>;
  variants: Record<string, Record<string, string>>;
  selectedSize: string;
  selectedColor: string;
  quantity: number;
  cartItems: CartItem[];
  hasBattery: boolean;
  batteryPrice: number;
  batteryQuantity: number;
  deliveryCharge: number;
  deliveryRates: { insideDhaka: number; outsideDhaka: number };
  whatsappNumber: string;
  userUuid: string;
  userIp: string;
  deviceType: 'Mobile' | 'Tablet' | 'Desktop';
  userOs: string;
  isBlocked: boolean;
  blockedIps: string[];
}

export class StateStore {
  private static instance: StateStore;
  
  public state: AppState = {
    sizes: {
      '6ft': {
        id: '6ft',
        name: '৬ ফিট (৭২ ইঞ্চি)',
        price: 390,
        oldPrice: 650,
        savings: 260,
        inStock: true
      },
      '12ft': {
        id: '12ft',
        name: '১২ ফিট (১৪৪ ইঞ্চি)',
        price: 690,
        oldPrice: 1200,
        savings: 510,
        inStock: true
      }
    },
    colors: {
      'light-pink': {
        id: 'light-pink',
        name: 'Light Pink (হালকা গোলাপি)',
        image: 'assets/images/6ft-light-pink.webp',
        hex: '#ffb3c6',
        inStock: true
      },
      'deep-pink': {
        id: 'deep-pink',
        name: 'Deep Pink (গাঢ় গোলাপি)',
        image: 'assets/images/6ft-deep-pink.webp',
        hex: '#ff4d6d',
        inStock: true
      },
      'white': {
        id: 'white',
        name: 'White (স্নো হোয়াইট)',
        image: 'assets/images/6ft-white.webp',
        hex: '#ffffff',
        inStock: true
      },
      'peach': {
        id: 'peach',
        name: 'Peach (পীচ কালার)',
        image: 'assets/images/6ft-peach.webp',
        hex: '#ffcaa7',
        inStock: true
      },
      'purple': {
        id: 'purple',
        name: 'Purple (রাউণ্ড পার্পল)',
        image: 'assets/images/6ft-purple.webp',
        hex: '#c77dff',
        inStock: true
      }
    },
    variants: {
      '6ft': {
        'light-pink': 'assets/images/6ft-light-pink.webp',
        'deep-pink': 'assets/images/6ft-deep-pink.webp',
        'white': 'assets/images/6ft-white.webp',
        'peach': 'assets/images/6ft-peach.webp',
        'purple': 'assets/images/6ft-purple.webp'
      },
      '12ft': {
        'light-pink': 'assets/images/12ft-light-pink.webp',
        'deep-pink': 'assets/images/12ft-deep-pink.webp',
        'white': 'assets/images/12ft-white.webp',
        'peach': 'assets/images/12ft-peach.webp',
        'purple': 'assets/images/12ft-purple.webp'
      }
    },
    selectedSize: '6ft',
    selectedColor: 'light-pink',
    quantity: 1,
    cartItems: [
      {
        id: 'item-1',
        size: '6ft',
        color: 'light-pink',
        quantity: 1,
        price: 390
      }
    ],
    hasBattery: false,
    batteryPrice: 60,
    batteryQuantity: 1,
    deliveryCharge: 70,
    deliveryRates: { insideDhaka: 70, outsideDhaka: 130 },
    whatsappNumber: '01303209784',
    userUuid: '',
    userIp: '',
    deviceType: 'Desktop',
    userOs: 'Unknown',
    isBlocked: false,
    blockedIps: []
  };

  public static getInstance(): StateStore {
    if (!StateStore.instance) {
      StateStore.instance = new StateStore();
    }
    return StateStore.instance;
  }

  public get currentSize(): ProductSize {
    return this.state.sizes[this.state.selectedSize] || this.state.sizes['12ft'];
  }

  public get currentColor(): ProductColor {
    return this.state.colors[this.state.selectedColor] || this.state.colors['light-pink'];
  }

  public get currentImage(): string {
    const size = this.state.selectedSize;
    const color = this.state.selectedColor;
    
    const isUploaded = (url?: string): boolean => !!(url && (url.includes('assets/images/uploads/') || url.startsWith('data:') || url.startsWith('http')));
    const variantImg = this.state.variants?.[size]?.[color];

    if (isUploaded(variantImg)) {
      return variantImg!;
    }

    const colorImg = this.state.colors?.[color]?.image;
    if (isUploaded(colorImg)) {
      return colorImg;
    }

    const otherSize = size === '6ft' ? '12ft' : '6ft';
    const otherVariantImg = this.state.variants?.[otherSize]?.[color];
    if (isUploaded(otherVariantImg)) {
      return otherVariantImg;
    }

    return variantImg || colorImg || 'assets/images/12ft-light-pink.webp';
  }

  public get subtotal(): number {
    if (this.state.cartItems && this.state.cartItems.length > 0) {
      return this.state.cartItems.reduce((acc, it) => acc + (it.price * it.quantity), 0);
    }
    return this.currentSize.price * this.state.quantity;
  }

  public get batteryTotal(): number {
    return this.state.hasBattery ? (this.state.batteryPrice * (this.state.batteryQuantity || 1)) : 0;
  }

  public get grandTotal(): number {
    return this.subtotal + this.batteryTotal + this.state.deliveryCharge;
  }
}
